import { motion } from 'framer-motion';
import { useInView } from '../hooks/useInView';
import { SERVICES, CORE_EDGE } from '../data/mock';
import { Ruler, Target, RefreshCcw, Wrench, Thermometer, Zap, Workflow, Globe } from 'lucide-react';

const serviceIconMap = {
  ruler: Ruler,
  target: Target,
  refreshCcw: RefreshCcw,
  wrench: Wrench,
};

const edgeIconMap = {
  thermometer: Thermometer,
  zap: Zap,
  workflow: Workflow,
  globe: Globe,
};

export const CoreEdge = () => {
  const [servicesRef, servicesInView] = useInView({ threshold: 0.1 });
  const [edgeRef, edgeInView] = useInView({ threshold: 0.1 });

  return (
    <>
      {/* Services Section */}
      <section id="services" ref={servicesRef} className="relative py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="text-center mb-16 max-w-3xl mx-auto"
          >
            <span className="text-[#D4952B] text-sm font-semibold tracking-[0.2em] uppercase">We Are Unique</span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0D1B2A] mt-3 leading-tight">
              Our Services
            </h2>
            <p className="text-2xl md:text-3xl text-[#0D1B2A]/30 font-semibold mt-2 font-['Noto_Sans_Arabic']">ننفرد بخدماتنا</p>
            <p className="text-[#1C2B33]/60 mt-4 text-base leading-relaxed">
              We are UNIQUE throughout our services — from precision design to lifelong maintenance.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {SERVICES.map((service, i) => {
              const Icon = serviceIconMap[service.icon];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.15 + i * 0.1 }}
                  className="group relative bg-[#FAFAF7] rounded-2xl p-8 border border-[#0D1B2A]/5 hover:border-[#D4952B]/30 transition-all duration-500 hover:shadow-xl hover:shadow-amber-900/5"
                >
                  {/* Hover accent line */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#D4952B] to-[#E8B84A] rounded-t-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <div className="flex items-start gap-5">
                    <div className="w-14 h-14 rounded-xl bg-[#0D1B2A]/5 group-hover:bg-[#D4952B]/10 flex items-center justify-center flex-shrink-0 transition-colors duration-500">
                      <Icon className="w-6 h-6 text-[#0B3D5C] group-hover:text-[#D4952B] transition-colors duration-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-[#0D1B2A] mb-1">{service.title}</h3>
                      <p className="text-[#0D1B2A]/30 text-sm font-medium mb-3 font-['Noto_Sans_Arabic']">{service.titleAr}</p>
                      <p className="text-[#1C2B33]/65 text-sm leading-relaxed">{service.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Core Edge Section */}
      <section ref={edgeRef} className="relative py-28 bg-[#0D1B2A] overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#D4952B]/5 rounded-full blur-3xl -translate-y-1/3 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#147DAB]/5 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4" />

        <div className="max-w-7xl mx-auto px-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={edgeInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="mb-16"
          >
            <span className="text-[#E8B84A] text-sm font-semibold tracking-[0.2em] uppercase">Advanced Experience</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-3 leading-tight">
              Our Core Edge
            </h2>
            <p className="text-2xl md:text-3xl text-white/25 font-semibold mt-2 font-['Noto_Sans_Arabic']">خبرة عملية بين يديك</p>
            <p className="text-white/40 mt-4 max-w-xl text-base leading-relaxed">
              Advanced experience provides the best of use — desert-proven engineering backed by decades of innovation.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {CORE_EDGE.map((edge, i) => {
              const Icon = edgeIconMap[edge.icon];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  animate={edgeInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.15 + i * 0.1 }}
                  className="group relative bg-white/[0.04] border border-white/8 rounded-2xl p-8 hover:bg-white/[0.08] hover:border-[#D4952B]/20 transition-all duration-500"
                >
                  <div className="flex items-start gap-5">
                    <div className="w-14 h-14 rounded-xl bg-white/5 group-hover:bg-[#D4952B]/15 flex items-center justify-center flex-shrink-0 transition-colors duration-500">
                      <Icon className="w-6 h-6 text-[#E8B84A] group-hover:text-[#D4952B] transition-colors duration-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-1 group-hover:text-[#E8B84A] transition-colors">{edge.title}</h3>
                      <p className="text-white/25 text-sm font-medium mb-3 font-['Noto_Sans_Arabic']">{edge.titleAr}</p>
                      <p className="text-white/50 text-sm leading-relaxed">{edge.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};
